import { cn } from '@portals-platform/ui';
import { AlertCircle, Plus, Trash2 } from 'lucide-react';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useFieldError } from '../lib/use-field-error.js';

export interface RepeaterRowApi {
  /** Zero-based position of this row. */
  index: number;
  /** Full RHF name for a field on this row, e.g. `owners.0.fullName`. */
  field: (name: string) => string;
}

interface RepeaterRowsProps {
  /** The array field's name, e.g. `owners`. */
  name: string;
  /** Renders one row's inputs. */
  children: (row: RepeaterRowApi) => React.ReactNode;
  /** A blank row. Give it an `id` — RHF needs stable keys. */
  makeEmpty: () => Record<string, unknown>;
  addLabel: string;
  /** Shown in place of the list when there are no rows. */
  emptyLabel: string;
  /** Heading for a row, e.g. `(i) => \`Owner ${i + 1}\``. */
  rowLabel: (index: number) => string;
  /** Rows below this cannot be removed. */
  min?: number;
  /** Adding is disabled at this many rows. */
  max?: number;
  /**
   * Fired after a row is removed, once RHF's array state has settled. Removal
   * happens inside this component, so a caller that persists on field blur would
   * otherwise never hear about a deletion.
   */
  onRemove?: () => void;
  className?: string;
}

/**
 * A repeating group of fields.
 *
 * Removal is a two-step inline confirm rather than a dialog: a modal for every
 * row is heavy, but a single misclick that silently discards a typed-out
 * director is worse.
 */
export function RepeaterRows({
  name,
  children,
  makeEmpty,
  addLabel,
  emptyLabel,
  rowLabel,
  min = 0,
  max,
  onRemove,
  className,
}: RepeaterRowsProps) {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name });
  const [pendingRemoval, setPendingRemoval] = React.useState<string | null>(null);

  // Array-level errors (a total that exceeds 100%, say) land on the array itself
  // or on its `root`, depending on how the resolver reports them. Both lookups
  // run unconditionally — `??` between two hook calls would skip the second.
  const directError = useFieldError(name);
  const rootError = useFieldError(`${name}.root`);
  const arrayError = directError ?? rootError;

  const atMax = max !== undefined && fields.length >= max;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {fields.length === 0 ? (
        <p className="rounded-[var(--radius-md)] border border-dashed border-input px-4 py-6 text-center text-[13px] text-muted-foreground">
          {emptyLabel}
        </p>
      ) : (
        <ul className="flex list-none flex-col gap-3 p-0">
          {fields.map((row, index) => {
            const confirming = pendingRemoval === row.id;
            const canRemove = fields.length > min;

            return (
              <li
                key={row.id}
                className="rounded-[var(--radius-md)] border border-border bg-background p-4"
              >
                <div className="mb-3 flex items-center justify-between gap-3">
                  <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                    {rowLabel(index)}
                  </span>

                  {canRemove &&
                    (confirming ? (
                      <span className="flex items-center gap-2">
                        <span className="text-[11px] text-muted-foreground">Remove this?</span>
                        <button
                          type="button"
                          onClick={() => {
                            remove(index);
                            setPendingRemoval(null);
                            // After RHF has applied the removal, not before.
                            queueMicrotask(() => onRemove?.());
                          }}
                          className="rounded-[var(--radius-sm)] px-2 py-1 text-[11px] font-medium text-destructive transition-colors hover:bg-destructive/10"
                        >
                          Remove
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingRemoval(null)}
                          className="rounded-[var(--radius-sm)] px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted"
                        >
                          Keep
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPendingRemoval(row.id)}
                        className="flex items-center gap-1.5 rounded-[var(--radius-sm)] px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        <Trash2 className="size-3" aria-hidden="true" />
                        Remove
                        <span className="sr-only">{rowLabel(index)}</span>
                      </button>
                    ))}
                </div>

                {children({
                  index,
                  field: (fieldName) => `${name}.${index}.${fieldName}`,
                })}
              </li>
            );
          })}
        </ul>
      )}

      {arrayError?.message && (
        <p className="flex items-center gap-1.5 text-[11px] font-medium text-destructive">
          <AlertCircle className="size-3 shrink-0" aria-hidden="true" />
          {arrayError.message}
        </p>
      )}

      <div>
        <button
          type="button"
          onClick={() => append(makeEmpty())}
          disabled={atMax}
          className={cn(
            'inline-flex items-center gap-2 rounded-[var(--radius-md)] border border-input px-3 py-2',
            'text-[13px] font-medium text-foreground transition-colors',
            'hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          <Plus className="size-3.5" aria-hidden="true" />
          {addLabel}
        </button>
      </div>
    </div>
  );
}
